import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import ScHeader from '../../components/ScHeader';
import { syncSleepCareRefundStatuses } from '../../config/sleepCareCheckout';
import './payments.css';

function userFromSupabaseAuthUser(authUser) {
  if (!authUser) return null;
  const meta = authUser.user_metadata || {};
  return {
    email: authUser.email,
    user_metadata: {
      full_name: meta.full_name ?? meta.name ?? authUser.email?.split('@')[0] ?? '',
      avatar_url: meta.avatar_url ?? meta.picture ?? null,
    },
  };
}

function formatMoney(n, currency) {
  if (n == null) return '—';
  const cur = currency || 'KRW';
  return `${Number(n).toLocaleString('ko-KR')} ${cur}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ko-KR');
  } catch {
    return iso;
  }
}

function getStatusMeta(row) {
  const paymentStatus = String(row.payment_status || '').toUpperCase();
  const refundStatus = String(row.refund_status || '').toUpperCase();
  const refundAmount = row.refund_amount != null ? Number(row.refund_amount) : null;
  const paymentAmount = row.payment_amount != null ? Number(row.payment_amount) : null;
  const isRefunded =
    paymentStatus === 'REFUNDED' ||
    paymentStatus === 'CANCELLED' ||
    ['COMPLETED', 'FULL', 'REFUNDED', 'DONE', 'SUCCESS'].includes(refundStatus) ||
    (refundAmount != null &&
      paymentAmount != null &&
      !Number.isNaN(refundAmount) &&
      !Number.isNaN(paymentAmount) &&
      refundAmount >= paymentAmount);

  if (isRefunded) {
    return { key: 'refunded', label: '환불 완료' };
  }
  if (paymentStatus === 'PAID' || paymentStatus === 'COMPLETED') {
    return { key: 'paid', label: '결제 완료' };
  }
  if (paymentStatus === 'FAILED') {
    return { key: 'failed', label: '결제 실패' };
  }
  if (paymentStatus === 'CANCELLED') {
    return { key: 'cancelled', label: '결제 취소' };
  }
  return {
    key: (row.payment_status || '').toLowerCase() || 'unknown',
    label: row.payment_status || '—',
  };
}

export default function Payments() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const saved = sessionStorage.getItem('mock_user');
    if (saved) setUser(JSON.parse(saved));

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const u = userFromSupabaseAuthUser(session.user);
        if (u) {
          sessionStorage.setItem('mock_user', JSON.stringify(u));
          setUser(u);
        }
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = userFromSupabaseAuthUser(session.user);
        if (u) {
          sessionStorage.setItem('mock_user', JSON.stringify(u));
          setUser(u);
        }
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadCheckouts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        setRows([]);
        setLoading(false);
        return;
      }

      await syncSleepCareRefundStatuses(session.user.id);

      const { data, error: qErr } = await supabase
        .from('checkouts')
        .select(
          `
          payment_id,
          order_name,
          payment_amount,
          currency,
          payment_status,
          refund_status,
          refund_amount,
          refunded_at,
          refund_reason,
          paid_at,
          cohort_care_option_id,
          card_company,
          card_number_masked,
          created_at,
          cohort_care_options ( cohort_number, cohort_label, title )
        `
        )
        .eq('auth_user_id', session.user.id)
        .order('created_at', { ascending: false });

      console.log('[payments] checkouts', { rowCount: data?.length, data, error: qErr });

      if (qErr) {
        setError(qErr.message);
        setRows([]);
      } else {
        setRows(data || []);
      }
    } catch (e) {
      setError(e.message || '조회 중 오류가 발생했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCheckouts();
  }, [loadCheckouts]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem('mock_user');
    setUser(null);
  };

  return (
    <div className="pay-root">
      <ScHeader
        user={user}
        onLoginClick={() => navigate('/')}
        onUserUpdate={(updated) => {
          sessionStorage.setItem('mock_user', JSON.stringify(updated));
          setUser(updated);
        }}
        onLogout={handleLogout}
      />

      <main className="pay-main">
        <div className="pay-container">
          <nav className="pay-breadcrumb">
            <Link to="/">홈</Link>
            <span className="pay-breadcrumb-sep">/</span>
            <span>결제 내역</span>
          </nav>

          <h1 className="pay-title">결제 내역</h1>
          <p className="pay-desc">솜니아 슬립케어 프로그램 결제 기록입니다.</p>

          {loading && <p className="pay-muted">불러오는 중…</p>}
          {error && <p className="pay-error">{error}</p>}

          {!loading && !error && rows.length === 0 && (
            <div className="pay-empty">
              <p>표시할 결제 내역이 없습니다.</p>
              {/* <p className="pay-muted pay-empty-hint">
                소셜 로그인 후 결제하신 경우에만 여기에 나타납니다.
              </p>
              <Link to="/#sc-pricing" className="pay-link-home">
                프로그램 안내 보기
              </Link> */}
            </div>
          )}

          {!loading && rows.length > 0 && (
            <div className="pay-table-wrap">
              <table className="pay-table">
                <thead>
                  <tr>
                    <th>결제일시</th>
                    <th>기수</th>
                    <th>타입</th>
                    <th>케어</th>
                    <th>금액</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.payment_id}>
                      <td data-label="결제일시">{formatDate(r.paid_at || r.created_at)}</td>
                      <td data-label="기수">{r.cohort_care_options?.cohort_number ?? '—'}기</td>
                      <td data-label="타입">{r.cohort_care_options?.cohort_label ?? '—'}</td>
                      <td data-label="케어">{r.cohort_care_options?.title ?? '—'}</td>
                      <td data-label="금액">{formatMoney(r.payment_amount, r.currency)}</td>
                      <td data-label="상태">
                        {(() => {
                          const statusMeta = getStatusMeta(r);
                          return (
                            <span className={`pay-badge pay-badge--${statusMeta.key}`}>
                              {statusMeta.label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
